import * as React from 'react';

import { useRequest } from 'ahooks';
import { Alert, Col, Divider, Flex, Form, Row, Select, Space } from 'antd';

import {
  AntdDateRangePicker,
  DecimalInputNumber,
  PrimitiveTreeSelect,
} from 'components';
import { getBrands, getCategories } from 'services';
import type { bulkUpdateProducts } from 'services';
import type { ProductAmountType, ProductPriceType, ProductStatus } from 'types';
import {
  generateSelectOptions,
  generateTreeSelectData,
  inputRule,
  localizeProductAmountType,
  localizeProductPriceType,
  localizeProductStatus,
  normalizeToString,
} from 'utils';

const productStatuses = [
  'Submitted',
  'Draft',
  'Private',
] satisfies ProductStatus[];
const productPriceTypes = ['Percentage', 'Price'] satisfies ProductPriceType[];
const productAmountTypes = [
  'Increase',
  'Decrease',
] satisfies ProductAmountType[];

type FieldType = Partial<
  Pick<
    Parameters<typeof bulkUpdateProducts>[number],
    'brandsIds' | 'categoriesIds' | 'statuses' | 'type' | 'amount'
  > &
    Record<'duration', [unknown, unknown]> &
    Record<'amountType', ProductAmountType>
>;

interface ProductsEditFormContentProps {
  priceType: ProductPriceType | undefined;
  setPriceType: React.Dispatch<
    React.SetStateAction<ProductPriceType | undefined>
  >;
  setPriceAmount: React.Dispatch<React.SetStateAction<number | undefined>>;
  setAmountType: React.Dispatch<React.SetStateAction<ProductAmountType>>;
  setDurationRange: React.Dispatch<React.SetStateAction<[string?, string?]>>;
}

const ProductsEditFormContent = ({
  priceType,
  setPriceType,
  setPriceAmount,
  setAmountType,
  setDurationRange,
}: ProductsEditFormContentProps) => {
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );
  const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
    cacheKey: 'brands',
  });

  const { categories = [] } = categoriesData ?? {};
  const { brands = [] } = brandsData ?? {};

  const brandsSelectOptions = generateSelectOptions(
    brands,
    'id',
    'title',
    'id',
  );
  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );

  const { label: priceTypeLabel } = localizeProductPriceType(priceType);

  return (
    <Row className='p-6'>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='نوع'
          name='type'
          rules={[inputRule.required]}
        >
          <Select<ProductPriceType | undefined>
            allowClear
            placeholder='نوع به‌روزرسانی را انتخاب کنید'
            options={productPriceTypes.map(type => {
              const { label } = localizeProductPriceType(type);
              return {
                label,
                value: type,
              };
            })}
            onChange={value => {
              setPriceType(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Space.Compact className='w-full'>
          <Form.Item<FieldType>
            label='مقدار'
            name='amount'
            className='flex-1'
            rules={[inputRule.required, inputRule.digit]}
          >
            <DecimalInputNumber
              disabled={!priceType}
              addonAfter={priceTypeLabel}
              max={priceType === 'Percentage' ? 100 : undefined}
              placeholder='مقدار به‌روزرسانی را وارد کنید'
              onChange={value => {
                if (value === null) return;

                setPriceAmount(Number(value));
              }}
            />
          </Form.Item>
          <Form.Item<FieldType> label='‌' name='amountType'>
            <Select<ProductAmountType | undefined>
              className='[&:not(.ant-select-disabled)_.ant-select-selector]:bg-[--ant-color-fill-quaternary] [&_.ant-select-selector]:border-[--ant-color-border] [&_.ant-select-selector]:border-s-transparent [&_.ant-select-selector]:shadow-none'
              disabled={!priceType}
              options={productAmountTypes.map(type => {
                const { label } = localizeProductAmountType(type);
                return {
                  label,
                  value: type,
                };
              })}
              onChange={value => {
                if (value) {
                  setAmountType(value);
                }
              }}
            />
          </Form.Item>
        </Space.Compact>
      </Col>
      <Col span={24}>
        <Divider dashed className='mb-4 mt-0' />
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='برندها' name='brandsIds'>
          <Select
            allowClear
            mode='multiple'
            placeholder='برندهای محصول‌ها را انتخاب کنید'
            loading={brandsLoading}
            options={brandsSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='دسته‌بندی‌ها' name='categoriesIds'>
          <PrimitiveTreeSelect
            multiple
            treeCheckable
            treeCheckStrictly
            placeholder='دسته‌بندی‌های محصول‌ها را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='وضعیت‌ها' name='statuses'>
          <Select
            allowClear
            mode='multiple'
            maxTagCount='responsive'
            placeholder='وضعیت‌های محصول‌ها را انتخاب کنید'
            options={productStatuses.map(type => {
              const { label } = localizeProductStatus(type);
              return {
                label,
                value: type,
              };
            })}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='بازه زمانی' name='duration'>
          <AntdDateRangePicker
            allowEmpty
            placeholder={[
              'تاریخ شروع ساخت محصول‌ها',
              'تاریخ پایان ساخت محصول‌ها',
            ]}
            onDateChange={([updatedLowerBound, updatedUpperBound]) => {
              setDurationRange([updatedLowerBound, updatedUpperBound]);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Flex vertical gap='small'>
          <Alert
            showIcon
            type='info'
            message='برای اعمال تغییرات، لطفاً حداقل یکی از فیلترها (برندها، دسته‌بندی‌ها، وضعیت‌ها یا بازه زمانی) را انتخاب یا تکمیل کنید.'
          />
        </Flex>
      </Col>
    </Row>
  );
};

export { ProductsEditFormContent };
